import { DateTime } from 'luxon';

import { InvoiceRowType } from '../../../common/application/model/enums/InvoiceRowType';
import { VehicleContract } from '../../../common/application/model/VehicleContract';
import { VehicleCostMonthlyRow } from '../../../common/application/model/VehicleCostMonthlyRow';
import { SalesInvoiceRow } from '../../../common/application/model/SalesInvoiceRow';
import { roundCurrencyValue } from '../../../node/application/service/financial_service';

export const calculateTotalVatFromMonthlyRows = (
    vehicleContractId: string,
    type: 'invoicedVat' | 'projectedVat' | 'paidVat',
    monthlyCostRows: { [vehicleContractId: string]: { [costType: string]: VehicleCostMonthlyRow } },
): number => {
    let sum = 0;

    if (!monthlyCostRows[vehicleContractId]) {
        return sum;
    }

    for (const cost of Object.values(monthlyCostRows[vehicleContractId])) {
        sum += cost[type] || 0;
    }

    return roundCurrencyValue(sum);
};

// Constructed once. These are called from table templates, so they run per cell per render -
// building a formatter each time was one of the more expensive things happening during a render.
const currencyFormat = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' });
const numberFormat = new Intl.NumberFormat('fi-FI');
const finnishDateFormat = new Intl.DateTimeFormat('fi-FI');

export const formatCurrency = (value: number | null | undefined) => {
    if (typeof value === 'undefined' || value === null) {
        return '-';
    } else {
        return currencyFormat.format(value);
    }
};

export const formatNumber = (value: number | null | undefined) => {
    if (typeof value === 'undefined' || value === null) {
        return '-';
    } else {
        return numberFormat.format(value);
    }
};

export const formatToFinnishDate = (value: string | null | undefined) => {
    if (typeof value === 'undefined' || value === null) {
        return '-';
    } else {
        return finnishDateFormat.format(new Date(value));
    }
};

export const sortAlphabetically = (sortProperty: string): ((itemA: any, itemB: any) => number) => {
    return (itemA: any, itemB: any) => {
        const nameA = (itemA[sortProperty] ?? '').toLowerCase();
        const nameB = (itemB[sortProperty] ?? '').toLowerCase();
        return nameA.localeCompare(nameB) as number;
    };
};

export const sortNumerically = (sortProperty: string) => {
    return (itemA: any, itemB: any) => {
        const numberA = itemA[sortProperty];
        const numberB = itemB[sortProperty];

        if (numberA < numberB) {
            return -1;
        }

        if (numberA > numberB) {
            return 1;
        }

        return 0;
    };
};

export const initialVehicleContractColumns: string[] = [
    'vehicleState',
    'contractInfo',
    'smartFleetContractNumber',
    'smartFleetContractStart',
    'smartFleetContractEnd',
    'vehicleRegistrationNumber',
    'vehicleMakeId',
    'vehicleModel',
    'customerId',
    'financingContractType',
    'financingContractFinancingVendorId',
    'costMonthlyProjected',
    'costMonthlyInvoiced',
];

export const ignoredVehicleContractColumns: string[] = [
    'financingContractRepurchaseVendorId',
    'financingContractInitialUsage',
    'financingContractContractNumber',
    'smartFleetContractInitialUsage',
    'vehicleOfferDocumentUrl',
    'financingContractCustomerExpenseAccountId',
    'smartFleetContractCustomerExpenseAccountId',
    'financingContractUsageBudget',
    'financingContractInitialFee',
    'financingContractMonthlyManagementFee',
    'financingContractMonthlyFee',
    'financingContractFirstPayment',
    'financingContractMonthlyPayment',
    'vehicleVatPercentage',
    'vehicleDeliveryFee',
    'vehicleDiscount',
    'vehicleCo2EmissionNedc',
    'modified',
    'created',
    'id',
    'vehicleMake',
    'ignoreRetroactiveRows',
];

export const searchAbleVehicleContractColumns: string[] = [
    'vehicleMake',
    'vehicleState',
    'vehicleModel',
    'vehicleRegistrationNumber',
];

export const searchAblePurchaseInvoiceRowsColumns: string[] = ['productName', 'description'];

export const mapMonthlyCostsToVehicleContract = (
    vehicleContract: VehicleContract,
    vehicleContractNumbers: { [vehicleContractId: string]: { [costType: string]: number } },
): VehicleContract => {
    let contractCopy: VehicleContract = {
        ...vehicleContract,
    };

    Object.keys(vehicleContractNumbers[vehicleContract.id]).forEach((key) => {
        switch (key) {
            case InvoiceRowType.SMARTFLEET_MONTHLY_FEE:
                contractCopy = {
                    ...contractCopy,
                    smartFleetContractMonthlyFee: vehicleContractNumbers[vehicleContract.id][key],
                };
                break;
            case InvoiceRowType.SMARTFLEET_INITIAL_FEE:
                contractCopy = {
                    ...contractCopy,
                    smartFleetContractInitialFee: vehicleContractNumbers[vehicleContract.id][key],
                };
                break;
            case InvoiceRowType.FINANCING_MONTHLY_FEE:
                contractCopy = {
                    ...contractCopy,
                    financingContractMonthlyFee: vehicleContractNumbers[vehicleContract.id][key],
                };
                break;
            case InvoiceRowType.FINANCING_MONTHLY_MANAGEMENT_FEE:
                contractCopy = {
                    ...contractCopy,
                    financingContractMonthlyManagementFee: vehicleContractNumbers[vehicleContract.id][key],
                };
                break;
            case InvoiceRowType.FINANCING_MONTHLY_PAYMENT_LEASING:
            case InvoiceRowType.FINANCING_MONTHLY_PAYMENT_PART_PAYMENT:
            case InvoiceRowType.FINANCING_MONTHLY_SERVICE_BUDGET:
                contractCopy = {
                    ...contractCopy,
                    financingContractMonthlyPayment: vehicleContractNumbers[vehicleContract.id][key],
                };
                break;
            default:
                console.warn('Could not map sum to any number on vehicle contract');
                console.warn('Key:', key);
                console.warn('Value', vehicleContractNumbers[vehicleContract.id][key]);
                break;
        }
    });

    return contractCopy;
};

export const calculateInvoiceRowSums = (
    row: SalesInvoiceRow,
): {
    sumVat: number;
    sumVatExcluded: number;
    sumVatIncluded: number;
} => {
    const sumVat = roundCurrencyValue((row.quantity * row.unitPrice * row.vatPercentage) / 100);
    const sumVatExcluded = roundCurrencyValue(row.quantity * row.unitPrice);
    const sumVatIncluded = roundCurrencyValue(sumVat + sumVatExcluded);

    return {
        sumVat,
        sumVatExcluded,
        sumVatIncluded,
    };
};

export const convertLocalDateToUtc = (date: Date): Date => {
    const utcOffset = DateTime.fromJSDate(date).offset;
    const utcStartOfDay = DateTime.fromJSDate(date).plus({ minutes: utcOffset }).startOf('day');

    return utcStartOfDay.toJSDate();
};
